<template>
    <grid
        ref="grid"
        v-if="authorized"
        :column-defs="columnDefs"
        :row-data="remoteLockLocks"
        :is-auto-height="false"
    >
        <responsive-button
            :icon="['fas', 'arrows-rotate']"
            @click="handleFetchLocksClick"
            variant="primary"
        >Fetch Locks</responsive-button>
        <responsive-button
            :icon="['fas', 'gear']"
            @click="handleSettingsClick"
            variant="primary"
        >Settings</responsive-button>
        <responsive-button
            :icon="['fas', 'right-from-bracket']"
            @click="handleDisconnectClick"
            variant="primary"
        >Disconnect</responsive-button>
        <disconnect-modal
            title="Disconnect RemoteLock account"
            description="Are you sure you want to disconnect your RemoteLock account? You will not be able to generate lock codes for your reservations any more."
            :modal-id="disconnectModalID"
            :disconnect="disconnect"
            @success-disconnect="load"
        />
        <settings-modal-form
            :modal-id="settingsModalID"
        />
    </grid>
    <div v-else-if="busy"></div>
    <div class="mt-1 ml-2" v-else>
        <b-col>
            <b-row>
                <responsive-button
                    :icon="['fas', 'right-to-bracket']"
                    :responsiveness="false"
                    @click="handleAuthorizeClick"
                    variant="primary"
                >Authorize Access to RemoteLock</responsive-button>
            </b-row>
        </b-col>
    </div>
</template>

<script>
    import API from '../../../../api';
    import {
        DOOR_LOCKS_CONFIG_ENDPOINT,
        DOOR_LOCKS_PROPERTIES_ENDPOINT,
        DOOR_LOCKS_REMOTE_LOCK_ACCOUNT_DISCONNECT_ENDPOINT,
        DOOR_LOCKS_REMOTE_LOCK_LOCKS_ENDPOINT,
        DOOR_LOCKS_REMOTE_LOCK_LOCK_BY_ID_ENDPOINT,
        DOOR_LOCKS_ROOMS_ENDPOINT,
    } from '../../../../endpoints';
    import { escapeHTML } from '../../../../utils/strings';
    import Grid from '../../../generic/grid/grid.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import DisconnectModal from '../common/disconnect-modal.vue';
    import SettingsModalForm from '../common/settings-modal-form.vue';
    import { formatResponseError } from '../../../../utils/api';

    export default {
        name: 'RemoteLockSettings',
        components: {
            DisconnectModal,
            Grid,
            ResponsiveButton,
            SettingsModalForm,
        },
        mounted() {
            if (this.$route.query.authSuccess) {
                if (Boolean(parseInt(this.$route.query.authSuccess))) {
                    this.successToast({text: 'Authorization done.'})
                } else {
                    this.errorToast({text: 'Authorization error.'})
                }
                this.$router.push('/app/door-lock/settings/remote-lock/');
            }

            this.load();
        },
        data() {
            return {
                authorized: false,
                busy: false,
                properties: [],
                remoteLockLocks: [],
                rooms: [],
                disconnectModalID: 'remote-lock-disconnect-modal',
                settingsModalID: 'remote-lock-settings-modal',
                columnDefs: [
                    {
                        field: 'name',
                        headerName: 'Name',
                        sort: 'asc',
                    },
                    {
                        field: 'type',
                        headerName: 'Type',
                    },
                    {
                        headerName: 'Property',
                        cellEditor: 'selectCellEditor',
                        editable: true,
                        cellClass: 'text-primary lx-editable-cell',
                        cellEditorParams: ({ data }) => {
                            const value = this.getPropertyRoomValue({ data });
                            const text = this.getPropertyRoomName({ data });

                            const options = [{ value: null, text: 'Not set' }];
                            if (!!value) {
                                options.push({ value, text });
                            }
                            options.push(
                                ...this.properties.map(p => ({
                                    value: `${p.id}`,
                                    text: p.name,
                                }))
                            );
                            options.push(
                                ...this.rooms.map(r => ({
                                    value: `${r.property_id}-${r.id}`,
                                    text: r.name,
                                }))
                            );

                            return { options };
                        },
                        valueGetter: ({ data }) => this.getPropertyRoomValue({ data }),
                        getQuickFilterText: ({ data }) => this.getPropertyRoomName({ data }),
                        cellRenderer: ({ data }) => escapeHTML(this.getPropertyRoomName({ data })),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            const [propertyID, roomID] = (newValue || '').split('-');
                            const [oldPropertyID, oldRoomID] = (oldValue || '').split('-');
                            const oldPropertyName = data.rental_property_name;
                            const oldRoomName = data.room_name;

                            (async () => {
                                try {
                                    await API.put(
                                        DOOR_LOCKS_REMOTE_LOCK_LOCK_BY_ID_ENDPOINT(data.id),
                                        {
                                            rental_property_id: (!!propertyID && !roomID) ? propertyID : null,
                                            room_id: !!roomID ? roomID : null,
                                        }
                                    );
                                    await Promise.all([
                                        this.fetchProperties(),
                                        this.fetchRooms(),
                                    ]);
                                } catch (error) {
                                    this.errorToast({ text: 'Could not save property.'})
                                    if (oldRoomID) {
                                        data.rental_property_id = null;
                                        data.room_id = oldRoomID;
                                        data.room_name = oldRoomName;
                                        data.room_rental_property_id = oldPropertyID;
                                    } else {
                                        data.rental_property_id = oldPropertyID;
                                        data.rental_property_name = oldPropertyName;
                                        data.room_id = null;
                                        data.room_rental_property_id = null;
                                    }
                                    node.setData(data);
                                }
                            })();

                            if (roomID) {
                                const room = this.rooms.find(r => r.id === parseInt(roomID));
                                data.rental_property_id = null;
                                data.room_id = roomID;
                                data.room_name = room && room.name;
                                data.room_rental_property_id = propertyID;
                            } else {
                                const property = this.properties.find(p => p.id === parseInt(propertyID));
                                data.rental_property_id = propertyID;
                                data.rental_property_name = property && property.name;
                                data.room_id = null;
                                data.room_rental_property_id = null;
                            }
                            return true;
                        },
                    }
                ]
            }
        },
        methods: {
            getPropertyRoomValue({ data }) {
                if (data) {
                    if (data.room_rental_property_id && data.room_id) {
                        return `${data.room_rental_property_id}-${data.room_id}`;
                    }
                    if (data.rental_property_id) {
                        return `${data.rental_property_id}`;
                    }
                }
                return null;
            },
            getPropertyRoomName({ data }) {
                if (data) {
                    if (data.room_rental_property_id && data.room_id) {
                        return data.room_name || 'No name';
                    }
                    if (data.rental_property_id) {
                        return data.rental_property_name || 'No name';
                    }
                }
                return 'Not set';
            },
            handleAuthorizeClick() {
                window.location = [
                    'https://connect.remotelock.com/oauth/authorize?client_id=',
                    window.REMOTE_LOCK_CLIENT_ID,
                    '&response_type=code&redirect_uri=',
                    window.REMOTE_LOCK_REDIRECT_URI,
                ].join('');
            },
            handleDisconnectClick() {
                this.$bvModal.show(this.disconnectModalID);
            },
            handleSettingsClick() {
                this.$bvModal.show(this.settingsModalID);
            },
            disconnect() {
                return API.post(DOOR_LOCKS_REMOTE_LOCK_ACCOUNT_DISCONNECT_ENDPOINT());
            },
            async load() {
                this.busy = true;
                this.$parent.$emit('busy', true);

                try {
                    await Promise.all([
                        this.fetchRemoteLockLocks(),
                        this.fetchProperties(),
                        this.fetchRooms(),
                    ]);
                } finally {
                    this.busy = false;
                    this.$parent.$emit('busy', false);
                }
            },
            async handleFetchLocksClick() {
                this.$parent.$emit('busy', true);
                try {
                    await this.refetchRemoteLockLocks();
                    await Promise.all([
                        this.fetchRemoteLockLocks(),
                        this.fetchProperties(),
                        this.fetchRooms(),
                    ]);
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async refetchRemoteLockLocks() {
                try {
                    await API.post(DOOR_LOCKS_REMOTE_LOCK_LOCKS_ENDPOINT());
                } catch (err) {
                    const { generic } = formatResponseError(err);
                    this.errorToast({ text: generic || 'Could not fetch remote locks.'})
                }
            },
            async fetchRemoteLockLocks() {
                try {
                    const response = await API.get(DOOR_LOCKS_REMOTE_LOCK_LOCKS_ENDPOINT());
                    this.remoteLockLocks = response.data;
                    this.authorized = true;
                } catch (err) {
                    if (err.response.status === 404) {
                        this.authorized = false;
                    } else {
                        this.errorToast({ text: 'Could not load remote locks.'})
                    }
                }
            },
            async fetchProperties() {
                try {
                    const response = await API.get(DOOR_LOCKS_PROPERTIES_ENDPOINT());
                    this.properties = response.data;
                } catch {
                    this.errorToast({ text: 'Could not load properties.'})
                }
            },
            async fetchRooms() {
                try {
                    const response = await API.get(DOOR_LOCKS_ROOMS_ENDPOINT());
                    this.rooms = response.data;
                } catch {
                    this.errorToast({ text: 'Could not load rooms.'})
                }
            },
        }
    }
</script>